import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Box, CircularProgress, Typography } from "@mui/material";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useNavigate } from "react-router-dom";
import { BASE_URL } from "../config/network/ApiUrl";

const ViewAllIssuesMenu = ({ setData }) => {
    const [menuData, setMenuData] = useState([]);
    const [hoveredYear, setHoveredYear] = useState(null);
    const [hoveredMonth, setHoveredMonth] = useState(null);
    const [menuOpen, setMenuOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const menuRef = useRef(null);
    const navigate = useNavigate();

    // Check if mobile device
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);

        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Close menu when clicking outside (for mobile)
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isMobile && menuRef.current && !menuRef.current.contains(event.target)) {
                setMenuOpen(false);
                setHoveredYear(null);
                setHoveredMonth(null);
            }
        };

        if (isMobile) {
            document.addEventListener('mousedown', handleClickOutside);
            document.addEventListener('touchstart', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, [isMobile]);

    // Fetch menu data from API
    useEffect(() => {
        const fetchMenu = async () => {
            try {
                const res = await axios.get(   
                    `${BASE_URL}api/menu/echo-mahindra/links`
                );
                if (res?.data && res?.data?.links) {
                    setMenuData(res?.data?.links);
                }
            } catch (err) {
                console.error("Error fetching menu:", err);
            }
        };

        fetchMenu();
    }, []);

    // Helper: convert text to slug for URL
    const toSlug = (text) =>
        text?.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

    // Toggle menu for mobile
    const toggleMenu = () => {
        if (isMobile) {
            setMenuOpen(!menuOpen);
            if (!menuOpen) {
                setHoveredYear(null);
                setHoveredMonth(null);
            }
        }
    };

    // Handle mouse enter/leave for desktop
    const handleMouseEnter = () => {
        if (!isMobile) {
            setMenuOpen(true);
        }
    };

    const handleMouseLeave = () => {
        if (!isMobile) {
            setMenuOpen(false);
            setHoveredYear(null);
            setHoveredMonth(null);
        }
    };

    const handleNavigation = async (year, month, issue) => {
        setLoading(true);
        try {   
            const res = await axios.get(`${BASE_URL}api/inside-pages?year=${year}&month=${month}&issue_number=${issue.split("-")[1]}`);
            setData(res?.data?.data ? res?.data?.data : []);
            const url = `/${year}/${toSlug(month)}/${toSlug(issue)}`; 
            navigate(url, { state: res?.data?.data });

            // Close menu on mobile after navigation
            // if (isMobile) {
            //     setMenuOpen(false);
            //     setHoveredYear(null);
            //     setHoveredMonth(null);
            // }
            setMenuOpen(false);
            setHoveredYear(null);
            setHoveredMonth(null);

        } catch (err) {
            console.error(err);
            setError("Failed to load images.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box
            ref={menuRef}
            sx={{ position: "relative", display: "inline-block" }}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            {/* Main Menu Title */}
            <Typography
                onClick={toggleMenu}
                sx={{
                    color: "#fff",
                    fontSize: 14,
                    cursor: "pointer",
                    textTransform: "uppercase",
                    fontWeight: 500,
                    userSelect: "none",
                    py: 1,
                    px: 1,
                }}
            >
                VIEW ALL ISSUES ▾
            </Typography>

            {/* Outer Dropdown */}
            <Box
                className="dropdown-menu"
                sx={{
                    display: menuOpen ? "block" : "none",
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    backgroundColor: "rgba(0,0,0,0.95)",
                    color: "#fff",
                    zIndex: 1000,
                    boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
                }}
            >
                {menuData?.map((year) => (
                    <Box
                        key={year.id}
                        sx={{
                            position: "relative",
                            px: 2,
                            py: 1,
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            transition: "background 0.2s",
                            "&:hover": { backgroundColor: isMobile ? "transparent" : "#d71920" },
                            backgroundColor: hoveredYear === year.title ? "#d71920" : "transparent",
                        }}
                        onMouseEnter={() => !isMobile && setHoveredYear(year?.title)}
                        onMouseLeave={() => !isMobile && setHoveredYear(null)}
                        onClick={() => isMobile && setHoveredYear(hoveredYear === year.title ? null : year.title)}
                    >
                        <Typography sx={{ fontSize: 14 }}>{year.title}</Typography>

                        {year?.children && year?.children?.length > 0 && (
                            <>
                                {(hoveredYear === year.title || (isMobile && hoveredYear === year.title)) ? (
                                    <KeyboardArrowRightIcon fontSize="small" />
                                ) : (
                                    <KeyboardArrowDownIcon fontSize="small" />
                                )}
                            </>
                        )}

                        {/* Months Submenu */}
                        {year?.children && year?.children?.length > 0 && (
                            <Box
                                sx={{
                                    display: hoveredYear === year.title || (isMobile && hoveredYear === year.title) ? "block" : "none",
                                    position: "absolute",
                                    top: 0,
                                    left: "100%",
                                    backgroundColor: "rgba(0,0,0,0.95)",
                                    boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
                                }}
                                className="monthDrop"
                            >
                                {year?.children?.map((month) => (
                                    <Box
                                        key={month.id}
                                        sx={{
                                            position: "relative",
                                            px: 2,
                                            py: 1,
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            "&:hover": { backgroundColor: isMobile ? "transparent" : "#d71920" },
                                            backgroundColor: hoveredMonth === month.title ? "#d71920" : "transparent",
                                        }}
                                        onMouseEnter={() => !isMobile && setHoveredMonth(month.title)}
                                        onMouseLeave={() => !isMobile && setHoveredMonth(null)}
                                        onClick={(e) => {
                                            if (isMobile) {
                                                e.stopPropagation();
                                                setHoveredMonth(hoveredMonth === month.title ? null : month.title);
                                            }
                                        }}
                                    >
                                        <Typography sx={{ fontSize: 14 }}>{month?.title}</Typography>

                                        {month?.children && month?.children?.length > 0 && (
                                            <>
                                                {(hoveredMonth === month.title || (isMobile && hoveredMonth === month.title)) ? (
                                                    <KeyboardArrowRightIcon fontSize="small" />
                                                ) : (
                                                    <KeyboardArrowDownIcon fontSize="small" />
                                                )}
                                            </>
                                        )}

                                        {/* Issues Submenu */}
                                        {month?.children && month?.children?.length > 0 && (
                                            <Box
                                                sx={{
                                                    display: hoveredMonth === month.title || (isMobile && hoveredMonth === month.title) ? "block" : "none",
                                                    position: "absolute",
                                                    top: 0,
                                                    left: "100%",
                                                    backgroundColor: "rgba(0,0,0,0.95)",
                                                    minWidth: 180,
                                                    boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
                                                }}
                                            >
                                                {month?.children?.map((issue) => (
                                                    <Typography
                                                        key={issue.id}
                                                        sx={{
                                                            px: 2,
                                                            py: 1,
                                                            cursor: "pointer",
                                                            fontSize: 13,
                                                            "&:hover": { backgroundColor: "#d71920" },
                                                        }}
                                                        onClick={(e) => {
                                                            if (isMobile) {
                                                                e.stopPropagation();
                                                            }
                                                            handleNavigation(
                                                                year.title,
                                                                month.title,
                                                                issue.title,
                                                            );
                                                        }}
                                                    >
                                                        {issue?.title?.toUpperCase()}
                                                    </Typography>
                                                ))}
                                            </Box>
                                        )}
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </Box>
                ))}
            </Box>

            {/* Remove all CSS hover logic - we'll handle it in JS */}
        </Box>
    );
};

export default ViewAllIssuesMenu;